import React from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import { BrowserRouter as Router, Route, Switch, Redirect } from 'react-router-dom';
import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import './firebaseui-styling.global.css';
import { GTMProvider } from '@elgorditosalsero/react-gtm-hook';
import { ISnippetsParams } from '@elgorditosalsero/react-gtm-hook/dist/models/GoogleTagManager';
import Login from './components/scenes/login';
import PrivateRoute from './components/private-route';
import AppThemeProvider from './mui-theme-provider';
import { AuthProvider, useUserAuth } from './contexts/auth';
import { ConfigProvider } from './contexts/config';
import { DialogsAndToastsProvider } from './contexts/dialogs-and-toasts';
import { MenuContextProvider } from './contexts/menu';
import Account from './components/scenes/account';
import UsersAndPermissionsScene from './components/scenes/users-and-permissions';
import SocialInteractionScene from './components/scenes/social-interaction';
import Articles from './components/scenes/articles';
import TimeMachine from './components/scenes/time-machine/time-machine';
import User from './components/scenes/user';
import MessageViewer from './components/scenes/message-viewer';
import WifiScene from './components/scenes/wifi';
import { SnackbarProvider } from 'notistack';
import Promos from './components/scenes/promos';
import Theme from './components/scenes/theme';
import LinksInfo from './components/scenes/links-info';
import VideoBranding from './components/scenes/video-branding';
import Support from './components/scenes/support';
import TeamData from './components/scenes/team-data';
import Arena from './components/scenes/arena';
import SceneConfig from './components/scenes/scene-config';
import PushDashboard from './components/scenes/push-dashboard';
import News from './components/scenes/news';
import TicketConfigurator from './components/scenes/ticket-config';
import Faq from './components/scenes/faq';
import StringsContext from './contexts/strings';
import svStrings from './sv-strings';
import Squad from './components/scenes/squad-database';
import AllPlayers from './components/scenes/all-players';
import Announcements from './components/scenes/announcements';
import ErrorBoundary from './components/error-boundary';
import Matches from './components/scenes/matches';
import Mappings from './components/scenes/mappings';
import Staff from './components/scenes/staff';
import Polls from './components/scenes/polls';
import Stories from './components/scenes/stories';
import AppMenu from './components/scenes/app-menu';
import FotbollPlay from './components/scenes/fotbollplay';

const gtmParams: ISnippetsParams = {
  id: process.env.REACT_APP_GOOGLE_TAG_MANAGER_ID!,
};

const FIVE_DAYS_MS = 5 * 24 * 60 * 60 * 1000;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: FIVE_DAYS_MS,
    },
  },
});

const persister = createSyncStoragePersister({
  storage: window.localStorage,
  key: 'superadmin-query-cache',
});
function Home() {
  const { isSuperAdmin, isTeamAdmin, isStatsAdmin } = useUserAuth();

  if (isTeamAdmin) {
    return <Redirect exact from="/" to="/scene-config" />;
  }

  if (isStatsAdmin) {
    return <Redirect exact from="/" to="/matches" />;
  }

  if (isSuperAdmin) {
    return <Redirect exact from="/" to="/scene-config" />;
  }

  return <Redirect exact from="/" to="/account" />;
}

function App() {
  return (
    <GTMProvider state={gtmParams}>
      <StringsContext.Provider value={{ strings: svStrings }}>
        <PersistQueryClientProvider
          client={queryClient}
          persistOptions={{
            persister,
            maxAge: FIVE_DAYS_MS,
            dehydrateOptions: {
              shouldDehydrateQuery: (query) => {
                // Only persist poll queries that have finished (using Infinity staleTime)
                const isPollQuery = query.queryKey[0] === 'poll';
                const hasInfiniteStaleTime = query.state.dataUpdatedAt > 0;
                return isPollQuery && hasInfiniteStaleTime && query.state.status === 'success';
              },
            },
          }}
        >
          <AppThemeProvider>
            <ErrorBoundary>
              <DialogsAndToastsProvider>
                <SnackbarProvider maxSnack={2}>
                  <AuthProvider>
                    <ConfigProvider>
                      <MenuContextProvider>
                        <CssBaseline />
                        <Router>
                          <Switch>
                            <Route exact path="/" component={Home} />
                            <Route path="/login" component={Login} />
                            <PrivateRoute path="/account" component={Account} />
                            <PrivateRoute path="/scene-config" component={SceneConfig} />
                            <PrivateRoute path="/promos/:id" component={Promos} />
                            <PrivateRoute path="/theme/:id" component={Theme} />
                            <PrivateRoute path="/links-info/:id" component={LinksInfo} />
                            <PrivateRoute path="/video-branding/:id" component={VideoBranding} />
                            <PrivateRoute path="/support/:id" component={Support} />
                            <PrivateRoute path="/arena/:id" component={Arena} />
                            <PrivateRoute path="/team-data/:id" component={TeamData} />
                            <PrivateRoute path="/fotbollplay/:id" component={FotbollPlay} />
                            <PrivateRoute path="/app-menu/:id" component={AppMenu} />
                            <PrivateRoute path="/message-viewer" component={MessageViewer} />
                            <PrivateRoute exact path="/time-machine" component={TimeMachine} />
                            <PrivateRoute path="/time-machine/:path" component={TimeMachine} />
                            <PrivateRoute path="/wifi" component={WifiScene} />
                            <PrivateRoute path="/squad" component={Squad} />
                            <PrivateRoute path="/all-players" component={AllPlayers} />
                            <PrivateRoute path="/announcements" component={Announcements} />
                            <PrivateRoute path="/staff" component={Staff} />
                            <PrivateRoute
                              path="/users-and-permissions"
                              component={UsersAndPermissionsScene}
                            />
                            <PrivateRoute path="/pushdashboard" component={PushDashboard} />
                            <PrivateRoute
                              path="/social-interaction"
                              component={SocialInteractionScene}
                            />
                            <PrivateRoute path="/articles" component={Articles} />
                            <PrivateRoute path="/matches" component={Matches} />
                            <PrivateRoute path="/mappings" component={Mappings} />
                            <PrivateRoute path="/news" component={News} />
                            <PrivateRoute path="/polls" component={Polls} />
                            <PrivateRoute path="/stories" component={Stories} />
                            <PrivateRoute path="/ticket-config" component={TicketConfigurator} />
                            <PrivateRoute path="/user" component={User} />
                            <PrivateRoute path="/faq" component={Faq} />
                          </Switch>
                        </Router>
                      </MenuContextProvider>
                    </ConfigProvider>
                  </AuthProvider>
                </SnackbarProvider>
              </DialogsAndToastsProvider>
            </ErrorBoundary>
          </AppThemeProvider>
        </PersistQueryClientProvider>
      </StringsContext.Provider>
    </GTMProvider>
  );
}

export default App;
